import React, { useContext, useEffect, useState } from 'react'
import {
  TouchableOpacity,
  FlatList, 
  StyleSheet,
  View,
  Text
} from 'react-native'
import theme from '../style/theme'
import api from '../api-client'
import { connect } from 'react-redux'
import { textStyles } from '../style'
import { Company, MatchedPlaidTransaction } from '@ecountabl/lib'
import { ThemedSearchBar } from '../utility/input/textinputs/themed-search-bar'
import { ThemeContext } from '../utility/context/ThemeContext'
import { MaterialIcons } from '@expo/vector-icons'
import { ThemedButton } from '../utility/input/buttons'
import Haptics from "../utility/haptics"
import Analytics from "../utility/analytics"

type Props = {
  tx: MatchedPlaidTransaction;
  onCancel: () => void;
  toggleAddBrand: (
    show: boolean,
    opts?: {isLocal?: boolean, tx?: MatchedPlaidTransaction}
  ) => void;
}

enum LoadingStage {
  None,
  Searching,
  Searched
}

const Feedback: React.FC<Props> = ({
  tx, onCancel, toggleAddBrand
}: Props) => {
  const [done, setDone] = useState(false)
  const [loadingStage, setLoadingStage] = useState(LoadingStage.None)
  const [error, setError] = useState<string | undefined>()
  const [mode, setMode] = useState<string | undefined>()
  const [suggestions, setSuggestions] = useState<Company[]>([])
  const [searchTerm, setSearchTerm] = useState('')

  const theme = useContext(ThemeContext)
  const { white, blue, green, gray, bgMain, textMain, borderColor } = theme.theme.colors

  const searchCompanies = (term) => {
    if (!term) {
      setSuggestions([])
      setError(undefined)
      setLoadingStage(LoadingStage.None)
      return
    }

    setLoadingStage(LoadingStage.Searching)
    api.tokenizedSearch(term, 3)
      .then((res) => {
        setSuggestions(res.companies)
        setLoadingStage(LoadingStage.Searched)
        setError(undefined)
      })
      .catch((ex) => {
        setError(ex.message)
        setLoadingStage(LoadingStage.Searched)
      })
  }

  const onChooseSuggestion = async (company: Company) => {
    setLoadingStage(LoadingStage.None)
    try {
      Analytics.track('user_transaction_feedback', {
        txid: tx._id,
        companyId: company._id,
        companyName: company.name,
      })
      await api.linkCompanyToTransaction(company._id, tx._id)
      setDone(true)
    } catch (ex) {
      setError(ex.message)
      setLoadingStage(LoadingStage.None)
    }
  }

  useEffect(() => {
    if (done) {
      setTimeout(() => {
        // close the feedback section after 2 seconds
        onCancel()
      }, 2000)
    }
  }, [done])

  if (done) {
    return (
      <View style={styles.feedback}>
        <Text style={[textStyles.body, {color: white, textAlign: "center"}]}>Thanks for submitting your feedback!</Text>
      </View>
    )
  }

  if (error) {
    return (
      <View style={styles.feedback}>
        <View style={styles.error}>
          <Text style={[textStyles.body, {color: white, marginBottom: 10, textAlign: "center"}]}>Oops, error during feedback</Text>
          <Text style={[textStyles.body, {color: white, textAlign: "center"}]}>{JSON.stringify(error)}</Text>
        </View>
      </View>
    )
  }

  if (mode === 'search') {
    return (
      <View style={[styles.feedback, { alignItems: 'stretch', flexGrow: 1 }]}>
        <Text style={[textStyles.body, {color: white, marginBottom: 10, textAlign: "center"}]}>
          Help improve results by linking this transaction to a business in our database!
        </Text>
        <View style={{}}>
          <ThemedSearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            search={searchCompanies}
            loading={loadingStage === LoadingStage.Searching}
            placeholder="Search for a business"
            textInputProps={{
              style: {
                backgroundColor: bgMain,
                color: textMain,
              },
              autoFocus: true,
              autoCorrect: false,
              placeholderTextColor: borderColor,
            }}

          />
        </View>
        {suggestions && (
          <View style={{height: "auto"}}>
            <View style={styles.suggestions}>
              {suggestions.length === 0 && loadingStage === LoadingStage.Searched ? (
                <Text style={[textStyles.body, {color: white, textAlign: "center"}]}>
                  No search results 🤷‍♀️
                </Text>
              ) : (
                <FlatList
                  data={suggestions}
                  keyExtractor={(item) => item._id}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      style={styles.suggestion}
                      onPress={() => {
                        Haptics.selection()
                        onChooseSuggestion(item)
                      }}
                    >
                      <Text style={[textStyles.body, {color: white}]}>{item.name}</Text>
                    </TouchableOpacity>
                  )}
                />
              )}
              <View style={styles.addBrand}>
                <Text style={[textStyles.body, {color: white, flex: 2, marginBottom: 0, textAlign: "center"}]}>
                  Still not seeing a great match?
                </Text>
                <ThemedButton
                  text="Add Business"
                  onPress={() => toggleAddBrand(true, {isLocal: true, tx})}
                  variant="icon"
                  hapticLevel="Soft"
                  containerStyle={{
                    flex: 1
                  }}
                  Icon={() => <MaterialIcons name="add-circle" size={20} color={white} />}
                />
              </View>
            </View>
          </View>
        )}
      </View>
    )
  }

  return (
    <View style={styles.feedback}>
      <Text style={[textStyles.body, {color: white, marginBottom: 10, textAlign: "center"}]}>
        Feel like this transaction should be handled differently?
      </Text>
      <View style={{ width: "100%", alignSelf: "center", gap: 8, flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <ThemedButton
          text="Local"
          onPress={() => {
            onCancel()
            toggleAddBrand(true, {isLocal: true, tx})
          }}
          variant="icon"
          hapticLevel="Soft"
          style={{
            backgroundColor: blue['700']
          }}
          containerStyle={{
            flex: 1,
            minWidth: 250
          }}
          Icon={() => <MaterialIcons name="store" size={20} color={white} />}
        />
        <ThemedButton
          text="Search"
          onPress={() => setMode('search')}
          variant="icon"
          hapticLevel="Soft"
          style={{
            backgroundColor: green['700']
          }}
          containerStyle={{
            flex: 1,
            minWidth: 250
          }}
          Icon={() => <MaterialIcons name="search" size={20} color={white} />}
        />
        <ThemedButton
          text="Cancel"
          onPress={onCancel}
          variant="icon"
          hapticLevel="Soft"
          style={{
            backgroundColor: gray['700']
          }}
          containerStyle={{
            flex: 1,
            minWidth: 250
          }}
          Icon={() => <MaterialIcons name="cancel" size={20} color={white} />}
        />
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  error: {
    backgroundColor: '#c92222', 
    padding: 10, 
    margin: 10, 
    borderRadius: 10
  },
  suggestion: {
    marginTop: 8,
    padding: 8,
    alignItems: 'center',
    backgroundColor: theme.colors.gray[600],
    borderRadius: 5
  },
  suggestions: {
    height: "auto"
  },
  addBrand: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
    padding: 10,
    margin: -15,
  },
  thanks: {
    padding: 10,
    fontSize: 20,
    fontWeight: 'bold',
    backgroundColor: '#666',
    borderRadius: 8
  },
  feedback: {
    height: "auto",
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    marginTop: 10,
    backgroundColor: theme.colors.gray["800"]
  }
})

const mapStateToProps = (state) => ({
  auth: state.auth
})

export default connect(mapStateToProps, { trackEvent: api.trackEvent })(Feedback)
